import { useState, useEffect, useCallback, useRef } from 'react';
import api from '@/lib/api';

interface PollDetail {
  pollId: string;
  createdBy: string;
  title: string;
  description: string | null;
  questions: Array<{
    questionId: string;
    text: string;
    type: string;
    options?: string[];
    required: boolean;
  }>;
  resultsVisibility: string;
  status: string;
  closesAt: string | null;
  responseCount: number;
  hasResponded: boolean;
  isCreator: boolean;
  createdAt: string;
  updatedAt: string;
}

interface UsePollReturn {
  poll: PollDetail | null;
  loading: boolean;
  error: string | null;
  refresh: () => void;
}

export function usePoll(pollId: string | undefined): UsePollReturn {
  const [poll, setPoll] = useState<PollDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const fetchIdRef = useRef(0);

  const fetchPoll = useCallback(async () => {
    if (!pollId) return;
    const id = ++fetchIdRef.current;
    try {
      setLoading(true);
      setError(null);
      const result = await api.getPoll(pollId);
      if (fetchIdRef.current !== id) return;
      setPoll(result);
    } catch (err) {
      if (fetchIdRef.current !== id) return;
      setError(err instanceof Error ? err.message : 'Failed to load poll');
    } finally {
      if (fetchIdRef.current === id) {
        setLoading(false);
      }
    }
  }, [pollId]);

  const refresh = useCallback(() => {
    fetchPoll();
  }, [fetchPoll]);

  useEffect(() => {
    refresh();
    return () => {
      fetchIdRef.current++;
    };
  }, [refresh]);

  return { poll, loading, error, refresh };
}
